import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function UserFormDialog({ open, onOpenChange, initial, onCreated, onUpdated, onGeneratedPassword, fixedRole }) {
  const isEdit = !!initial;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    email: "",
    role: "studente",
    first_name: "",
    last_name: "",
    password: "",
    fiscal_code: "",
    birth_date: "",
    phone: "",
  });

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          email: initial.email || "",
          role: initial.role || "studente",
          first_name: initial.first_name || "",
          last_name: initial.last_name || "",
          password: "",
          fiscal_code: initial.fiscal_code || "",
          birth_date: initial.birth_date || "",
          phone: initial.phone || "",
        });
      } else {
        setForm({
          email: "",
          role: fixedRole || "studente",
          first_name: "",
          last_name: "",
          password: "",
          fiscal_code: "",
          birth_date: "",
          phone: "",
        });
      }
    }
  }, [open, initial, fixedRole]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const body = {
          first_name: form.first_name,
          last_name: form.last_name,
          fiscal_code: form.fiscal_code || null,
          birth_date: form.birth_date || null,
          phone: form.phone || null,
          role: form.role,
        };
        const { data } = await api.patch(`/admin/users/${initial.id}`, body);
        toast.success("Utente aggiornato");
        onUpdated?.(data);
        onOpenChange(false);
      } else {
        // Studenti: CF e data di nascita obbligatori (finiscono sull'attestato).
        if (form.role === "studente") {
          const cf = (form.fiscal_code || "").trim().toUpperCase();
          if (!/^[A-Z0-9]{16}$/.test(cf)) {
            toast.error("Per gli studenti il codice fiscale è obbligatorio (16 caratteri).");
            return;
          }
          if (!form.birth_date) {
            toast.error("Per gli studenti la data di nascita è obbligatoria.");
            return;
          }
        }
        const body = {
          email: form.email,
          role: form.role,
          first_name: form.first_name,
          last_name: form.last_name,
          fiscal_code: form.fiscal_code || null,
          birth_date: form.birth_date || null,
          phone: form.phone || null,
        };
        if (form.password) body.password = form.password;
        const { data } = await api.post("/admin/users", body);
        onCreated?.(data.user);
        // Percorso primario: l'utente imposta la password dal link d'invito
        // ricevuto via email. La password generata resta solo come ripiego se
        // l'email non dovesse arrivare.
        toast.success(`Utente creato. Invito inviato a ${data.user.email}.`);
        if (data.generated_password) {
          onGeneratedPassword?.({
            email: data.user.email,
            password: data.generated_password,
            title: "Invito inviato",
            subtitle: `${data.user.email} riceverà un'email per impostare la propria password e accedere. Comunica la password qui sotto solo come ripiego, se l'email non arriva.`,
          });
        }
        onOpenChange(false);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="user-form-dialog" className="max-w-lg rounded-sm">
        <DialogHeader>
          <DialogTitle className="wordmark text-brand-blue">
            {isEdit
              ? "Modifica utente"
              : fixedRole === "studente" ? "Nuovo utente privato"
              : fixedRole === "formatore" ? "Nuovo formatore"
              : fixedRole === "super_admin" ? "Nuovo amministratore"
              : "Nuovo utente"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "L’email non è modificabile. Per la password usa 'Reset password'."
              : "L’utente riceverà un’email con un link per impostare la propria password e attivare l’account. Il campo password è opzionale: di norma lascialo vuoto."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input
              data-testid="uf-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-1.5 h-10 rounded-sm"
              disabled={isEdit}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nome</Label>
              <Input
                data-testid="uf-first-name"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="mt-1.5 h-10 rounded-sm"
                required
              />
            </div>
            <div>
              <Label>Cognome</Label>
              <Input
                data-testid="uf-last-name"
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="mt-1.5 h-10 rounded-sm"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {(isEdit || !fixedRole) && (
              <div>
                <Label>Ruolo</Label>
                <Select
                  value={form.role}
                  onValueChange={(v) => setForm({ ...form, role: v })}
                >
                  <SelectTrigger data-testid="uf-role" className="mt-1.5 h-10 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="studente">Studente</SelectItem>
                    <SelectItem value="formatore">Formatore</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isEdit && (
              <div>
                <Label>Password (opzionale)</Label>
                <Input
                  data-testid="uf-password"
                  type="text"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Vuota: la imposta l’utente via invito"
                  className="mt-1.5 h-10 rounded-sm"
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Codice fiscale{form.role === "studente" ? "" : " (opzionale)"}</Label>
              <Input
                data-testid="uf-fiscal-code"
                value={form.fiscal_code}
                onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })}
                maxLength={16}
                placeholder={form.role === "studente" ? "Obbligatorio (16 caratteri)" : "Opzionale"}
                required={!isEdit && form.role === "studente"}
                className="mt-1.5 h-10 rounded-sm font-mono uppercase"
              />
            </div>
            <div>
              <Label>Data di nascita{form.role === "studente" ? "" : " (opzionale)"}</Label>
              <Input
                data-testid="uf-birth-date"
                type="date"
                value={form.birth_date}
                onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                required={!isEdit && form.role === "studente"}
                className="mt-1.5 h-10 rounded-sm"
              />
            </div>
          </div>
          <div>
            <Label>Telefono</Label>
            <Input
              data-testid="uf-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+39 333 1234567"
              className="mt-1.5 h-10 rounded-sm"
            />
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm text-brand-muted hover:text-brand-ink"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="uf-submit"
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? "Salva modifiche" : "Crea utente"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
